import { scoreChapter1 } from '@neopolis/engine';
import type { Content, GameState, Problem } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * La fiche « Rapport de diagnostic », générée à partir de l'état de la partie.
 *
 * C'est la trace écrite du chapitre : elle se lit à l'écran et s'imprime. Les
 * boutons et l'habillage disparaissent à l'impression (`print:hidden`), pour
 * qu'une feuille A4 ne gaspille ni encre ni place.
 */
export function RapportDiagnostic({
  content,
  partie,
  onEcrireTrace,
  onFermer,
  onChapitreSuivant,
  chapitreSuivantOuvert,
}: {
  content: Content;
  partie: GameState;
  onEcrireTrace: (fieldId: string, valeur: string) => void;
  onFermer: () => void;
  /**
   * Passage au chapitre 2. En phase 6, ce déblocage reviendra à l'enseignant
   * pour toute la classe ; en attendant, l'îlot avance seul.
   */
  onChapitreSuivant: () => void;
  /**
   * Le chapitre 2 est-il ouvert ? `null` quand on l'ignore — hors ligne, ou
   * sans serveur. On n'enferme alors personne : le bouton reste actif.
   */
  chapitreSuivantOuvert: boolean | null;
}) {
  const chapitre = content.chapitres.find((c) => c.id === 'ch1');
  const parId = new Map(content.problemes.map((p) => [p.id, p]));
  const categorieNom = (id: string): string =>
    content.categories_problemes.find((c) => c.id === id)?.nom ?? id;
  const quartierNom = (id: string): string => content.quartiers.find((q) => q.id === id)?.nom ?? id;

  const decouverts = partie.discoveredProblems
    .map((id) => parId.get(id))
    .filter((p): p is Problem => p !== undefined);

  const score = scoreChapter1(partie, content);
  const champDefinition = chapitre?.trace_ecrite.champs.find((f) => f.id === 'definition');
  const champJustification = chapitre?.trace_ecrite.champs.find((f) => f.id === 'justification');

  return (
    <article className="rounded-2xl border-2 border-slate-300 bg-white p-5 print:border-0 print:p-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            {chapitre?.trace_ecrite.titre ?? T.rapport.titre}
          </h2>
          <p className="text-sm text-slate-600">
            {T.rapport.ilot} {partie.islandCode}
          </p>
        </div>

        <div className="flex gap-2 print:hidden">
          <button
            type="button"
            onClick={() => globalThis.print()}
            className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
          >
            {T.rapport.imprimer}
          </button>
          <button
            type="button"
            onClick={onFermer}
            className="min-h-touch rounded-xl bg-slate-100 px-4 font-semibold text-slate-700"
          >
            {T.rapport.fermer}
          </button>
        </div>
      </header>

      {champDefinition && (
        <section className="mt-5">
          <label htmlFor="trace-definition" className="block font-bold text-slate-900">
            {champDefinition.label}
          </label>
          <textarea
            id="trace-definition"
            rows={2}
            value={partie.writtenTrace.ch1['definition'] ?? ''}
            onChange={(evenement) => onEcrireTrace('definition', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5 overflow-x-auto">
        <h3 className="font-bold text-slate-900">
          {chapitre?.trace_ecrite.champs.find((f) => f.id === 'tableau_problemes')?.label}
        </h3>
        <table className="mt-2 w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="py-2 pr-3">{T.rapport.colonneProbleme}</th>
              <th className="py-2 pr-3">{T.rapport.colonneQuartier}</th>
              <th className="py-2 pr-3">{T.rapport.colonneCategorie}</th>
              <th className="py-2">{T.rapport.colonneConnecte}</th>
            </tr>
          </thead>
          <tbody>
            {decouverts.map((probleme) => (
              <tr key={probleme.id} className="border-b border-slate-200">
                <td className="py-2 pr-3 font-medium text-slate-900">{probleme.titre}</td>
                <td className="py-2 pr-3 text-slate-700">{quartierNom(probleme.quartier)}</td>
                <td className="py-2 pr-3 text-slate-700">
                  {categorieNom(partie.classification[probleme.id] ?? '')}
                </td>
                <td className="py-2 text-slate-700">
                  {partie.connectedAnalysis[probleme.id] ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-5">
        <h3 className="font-bold text-slate-900">
          {chapitre?.trace_ecrite.champs.find((f) => f.id === 'priorites')?.label}
        </h3>
        <ol className="mt-1 list-inside list-decimal text-slate-800">
          {partie.priorities.map((problemId) => (
            <li key={problemId}>{parId.get(problemId)?.titre ?? problemId}</li>
          ))}
        </ol>
      </section>

      {champJustification && (
        <section className="mt-5">
          <label htmlFor="trace-justification" className="block font-bold text-slate-900">
            {champJustification.label}
          </label>
          <textarea
            id="trace-justification"
            rows={4}
            value={partie.writtenTrace.ch1['justification'] ?? ''}
            onChange={(evenement) => onEcrireTrace('justification', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5 rounded-xl bg-amber-50 p-4 print:border print:border-slate-300">
        <h3 className="text-sm font-bold uppercase tracking-wide text-amber-800">
          {T.rapport.aRetenir}
        </h3>
        <p className="mt-1 text-slate-800">{chapitre?.trace_ecrite.a_retenir}</p>
      </section>

      <footer className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="print:hidden">
          <button
            type="button"
            disabled={chapitreSuivantOuvert === false}
            onClick={onChapitreSuivant}
            className="min-h-touch rounded-xl bg-emerald-600 px-6 font-semibold text-white hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500"
          >
            {T.rapport.chapitreSuivant}
          </button>
          {chapitreSuivantOuvert === false && (
            <p className="mt-1 text-sm text-slate-600">{T.rapport.chapitreVerrouille}</p>
          )}
        </div>
        <p className="font-semibold text-slate-800">
          {T.rapport.score} : {score.total} {T.rapport.points}
        </p>
      </footer>
    </article>
  );
}
